import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import api from '../../services/api';
import { diocesesService, paroissesService } from '../../services/api';
import Select from 'react-select';
import PaysModal from '../pays/PaysModal';
import ParentModal from '../parents/ParentModal';
import Swal from 'sweetalert2';
import { useTheme } from '../../contexts/ThemeContext';

const sexeOptions = [
  { value: 'M', label: 'Masculin' },
  { value: 'F', label: 'Féminin' }
];
const typeSejourOptions = [
  { value: 'externe', label: 'Externe' },
  { value: 'interne', label: 'Interne' }
];

const EleveModal = ({ eleve, onClose, onSuccess }) => {
  const { register, handleSubmit, setValue, reset, watch, formState: { errors } } = useForm();
  const { theme } = useTheme();
  const [parents, setParents] = useState([]);
  const [selectedParents, setSelectedParents] = useState([]);
  const [initialParents, setInitialParents] = useState([]); // Pour différencier ajouts/suppressions
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(eleve?.photo || '');
  const [photoFile, setPhotoFile] = useState(null);
  const [paysList, setPaysList] = useState([]);
  const [showPaysModal, setShowPaysModal] = useState(false);
  const [showParentModal, setShowParentModal] = useState(false);
  const [dioceses, setDioceses] = useState([]);
  const [selectedDiocese, setSelectedDiocese] = useState(null);
  const [selectedParoisse, setSelectedParoisse] = useState(null);
  const [paroissesCache, setParoissesCache] = useState([]);

  // Styles pour react-select selon le thème
  const selectStyles = useMemo(() => ({
    control: (base) => ({
      ...base,
      backgroundColor: theme === 'dark' ? '#374151' : '#fff',
      borderColor: theme === 'dark' ? '#4B5563' : '#D1D5DB',
      color: theme === 'dark' ? '#E5E7EB' : '#111827',
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: theme === 'dark' ? '#374151' : '#fff',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused 
        ? (theme === 'dark' ? '#4B5563' : '#E5E7EB')
        : (theme === 'dark' ? '#374151' : '#fff'),
      color: theme === 'dark' ? '#E5E7EB' : '#111827',
    }),
    singleValue: (base) => ({
      ...base,
      color: theme === 'dark' ? '#E5E7EB' : '#111827',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: theme === 'dark' ? '#4B5563' : '#E5E7EB',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: theme === 'dark' ? '#E5E7EB' : '#111827',
    }),
    input: (base) => ({
      ...base,
      color: theme === 'dark' ? '#E5E7EB' : '#111827',
    }),
  }), [theme]);

  // Charger les données de base (parents, pays, diocèses) une seule fois
  useEffect(() => {
    const fetchParents = async () => {
      const res = await api.get('/parents?limit=1000');
      setParents(res.data.data || []);
    };
    const fetchPays = async () => {
      const res = await api.get('/pays');
      setPaysList(res.data.data || []);
    };
    const fetchDioceses = async () => {
      const res = await diocesesService.getAll();
      setDioceses(res.data.data || []);
    };
    fetchParents();
    fetchPays();
    fetchDioceses();
  }, []);

  // Charger les données de l'élève et configurer les sélections après que les diocèses soient chargés
  useEffect(() => {
    if (!eleve) {
      reset({});
      setPhotoPreview('');
      setSelectedParents([]);
      setInitialParents([]);
      setSelectedDiocese(null);
      setSelectedParoisse(null);
      setParoissesCache([]);
      return;
    }

    reset(eleve);
    setPhotoPreview(eleve.photo || '');
    
    // Charger les données complètes de l'élève depuis l'API
    const loadEleveData = async () => {
      try {
        const res = await api.get(`/eleves/${eleve.id}`);
        const eleveData = res.data.data;
        const parentIds = (eleveData.parents || []).map(p => String(p.id));
        setSelectedParents(parentIds);
        setInitialParents(parentIds);
        
        // Attendre que les diocèses soient chargés avant de configurer les sélections
        const setupDioceseAndParoisse = async () => {
          if (!eleveData.paroisse_id || !eleveData.diocese_id) {
            return;
          }

          // Attendre que les diocèses soient disponibles
          let attempts = 0;
          const maxAttempts = 50; // 5 secondes max (50 * 100ms)
          
          const waitForDioceses = () => {
            return new Promise((resolve) => {
              const check = setInterval(() => {
                attempts++;
                if (dioceses.length > 0 || attempts >= maxAttempts) {
                  clearInterval(check);
                  resolve();
                }
              }, 100);
            });
          };

          await waitForDioceses();

          // Normaliser les IDs pour la comparaison (convertir en nombres si possible)
          const normalizeId = (id) => {
            if (id === null || id === undefined) return null;
            const numId = Number(id);
            return isNaN(numId) ? id : numId;
          };

          const eleveDioceseId = normalizeId(eleveData.diocese_id);
          const eleveParoisseId = normalizeId(eleveData.paroisse_id);

          // Maintenant que les diocèses sont chargés, trouver le diocèse
          // Comparer avec les IDs normalisés
          const diocese = dioceses.find(d => {
            const dId = normalizeId(d.id);
            return dId === eleveDioceseId || String(dId) === String(eleveDioceseId);
          });

          if (diocese) {
            const dioceseId = normalizeId(diocese.id);
            setSelectedDiocese({ value: dioceseId, label: diocese.diocese });
            setValue('diocese_id', dioceseId);
            
            // Charger les paroisses du diocèse pour le cache
            try {
              const parListRes = await paroissesService.getAll({ diocese_id: dioceseId });
              const paroissesList = parListRes.data.data || [];
              setParoissesCache(paroissesList);
              
              // Sélectionner la paroisse avec comparaison normalisée
              const paroisse = paroissesList.find(p => {
                const pId = normalizeId(p.id);
                return pId === eleveParoisseId || String(pId) === String(eleveParoisseId);
              });

              if (paroisse) {
                const paroisseId = normalizeId(paroisse.id);
                setSelectedParoisse({ value: paroisseId, label: paroisse.paroisse });
                setValue('paroisse_id', paroisseId);
              } else if (eleveData.paroisse_nom) {
                // Fallback : utiliser le nom de la paroisse si disponible
                setSelectedParoisse({ value: eleveParoisseId, label: eleveData.paroisse_nom });
                setValue('paroisse_id', eleveParoisseId);
              }
            } catch (error) {
              console.error('Erreur lors du chargement des paroisses:', error);
              // En cas d'erreur, utiliser les données disponibles
              if (eleveData.paroisse_nom) {
                setSelectedParoisse({ value: eleveParoisseId, label: eleveData.paroisse_nom });
                setValue('paroisse_id', eleveParoisseId);
              }
            }
          } else {
            console.warn('Diocèse non trouvé pour l\'ID:', eleveDioceseId, 'Diocèses disponibles:', dioceses.map(d => ({ id: d.id, type: typeof d.id })));
            // Si le diocèse n'est pas trouvé mais qu'on a les noms, utiliser les noms
            if (eleveData.diocese_nom && eleveDioceseId) {
              setSelectedDiocese({ value: eleveDioceseId, label: eleveData.diocese_nom });
              setValue('diocese_id', eleveDioceseId);
            }
            if (eleveData.paroisse_nom && eleveParoisseId) {
              setSelectedParoisse({ value: eleveParoisseId, label: eleveData.paroisse_nom });
              setValue('paroisse_id', eleveParoisseId);
            }
          }
        };

        await setupDioceseAndParoisse();
      } catch (error) {
        console.error('Erreur lors du chargement des données de l\'élève:', error);
      }
    };

    loadEleveData();
  }, [eleve, reset, setValue, dioceses]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setPhotoFile(null);
      setPhotoPreview('');
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview('');
    setValue('photo', '');
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      let eleveId = eleve ? eleve.id : null;
      const isNewEleve = !eleve;
      if (photoFile) {
        data.photo = photoPreview;
      }
      if (eleve) {
        await api.put(`/eleves/${eleve.id}`, data);
      } else {
        const res = await api.post('/eleves', data);
        eleveId = res.data.id;
      }
      // Gestion des liaisons parents
      // Ajouts
      const toAdd = selectedParents.filter(pid => !initialParents.includes(pid));
      // Suppressions
      const toRemove = initialParents.filter(pid => !selectedParents.includes(pid));
      await Promise.all([
        ...toAdd.map(parentId => api.post(`/parents/${parentId}/link-eleve`, { eleve_id: eleveId })),
        ...toRemove.map(parentId => api.delete(`/parents/${parentId}/unlink-eleve/${eleveId}`)),
      ]);
      
      // Afficher SweetAlert de succès
      if (isNewEleve) {
        await Swal.fire({
          icon: 'success',
          title: 'Élève ajouté',
          text: 'Le nouvel élève a bien été enregistré !',
          timer: 2500,
          showConfirmButton: false
        });
      } else {
        await Swal.fire({
          icon: 'success',
          title: 'Élève modifié',
          text: 'Les modifications de l\'élève ont été enregistrées avec succès !',
          timer: 2500,
          showConfirmButton: false
        });
      }
      
      onSuccess();
    } catch (e) {
      // Gérer l'erreur avec SweetAlert
      const errorMessage = e.response?.data?.message || "Erreur lors de l'enregistrement de l'élève";
      await Swal.fire({
        icon: 'error',
        title: 'Erreur',
        html: errorMessage.replace(/\n/g, '<br>'),
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  };

  // Options react-select pour les pays
  const paysOptions = paysList.map(pays => ({ value: pays.nom_pays, label: pays.nom_pays }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-6xl">
        <h2 className="text-xl font-semibold mb-4 dark:text-gray-100">
          {eleve ? 'Modifier' : 'Nouvel'} élève
        </h2>
        <form onSubmit={handleSubmit(onSubmit)}>
          <fieldset className="mb-6 border-b dark:border-gray-700 pb-4">
            <legend className="text-lg font-semibold mb-2 dark:text-gray-200">Informations élève</legend>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom *</label>
                <input {...register('nom', { required: true })} className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                {errors.nom && <span className="text-red-500 dark:text-red-400 text-xs mt-1">Champ obligatoire</span>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prénom *</label>
                <input {...register('prenom', { required: true })} className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                {errors.prenom && <span className="text-red-500 dark:text-red-400 text-xs mt-1">Champ obligatoire</span>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sexe *</label>
                <Select
                  options={sexeOptions}
                  value={sexeOptions.find(opt => opt.value === watch('sexe')) || null}
                  onChange={opt => setValue('sexe', opt ? opt.value : '')}
                  isClearable
                  placeholder="Sélectionner"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                />
                {errors.sexe && <span className="text-red-500 dark:text-red-400 text-xs mt-1">Champ obligatoire</span>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de naissance *</label>
                <input type="date" {...register('date_naissance', { required: true })} className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                {errors.date_naissance && <span className="text-red-500 dark:text-red-400 text-xs mt-1">Champ obligatoire</span>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type de séjour *</label>
                <Select
                  options={typeSejourOptions}
                  value={typeSejourOptions.find(opt => opt.value === watch('type_sejour')) || null}
                  onChange={opt => setValue('type_sejour', opt ? opt.value : '')}
                  isClearable
                  placeholder="Sélectionner"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                />
                {errors.type_sejour && <span className="text-red-500 dark:text-red-400 text-xs mt-1">Champ obligatoire</span>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 dark:file:bg-primary-900 file:text-primary-700 dark:file:text-primary-300 hover:file:bg-primary-100 dark:hover:file:bg-primary-800"
                />
                {photoPreview && (
                  <div className="mt-2">
                    <img src={photoPreview} alt="Aperçu" className="h-24 rounded shadow" />
                    <button type="button" className="text-xs text-red-600 dark:text-red-400 mt-1" onClick={removePhoto}>Supprimer</button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lieu de naissance</label>
                <input {...register('lieu_naissance')} className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nationalité</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Select
                      options={paysOptions}
                      value={paysOptions.find(opt => opt.value === watch('nationalite')) || null}
                      onChange={opt => setValue('nationalite', opt ? opt.value : '')}
                      isClearable
                      placeholder="Sélectionner un pays"
                      classNamePrefix="react-select"
                      styles={selectStyles}
                    />
                  </div>
                  <button
                    type="button"
                    className="px-2 py-1 bg-primary-600 text-white rounded hover:bg-primary-700"
                    onClick={() => setShowPaysModal(true)}
                    title="Ajouter un pays"
                  >+
                  </button>
                </div>
                {showPaysModal && (
                  <PaysModal
                    onClose={() => setShowPaysModal(false)}
                    onSuccess={async (newPays) => {
                      setShowPaysModal(false);
                      const res = await api.get('/pays');
                      setPaysList(res.data.data || []);
                      if (newPays && newPays.nom_pays) {
                        setValue('nationalite', newPays.nom_pays, { shouldValidate: true });
                      }
                    }}
                  />
                )}
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Adresse</label>
                <input {...register('adresse')} className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Téléphone</label>
                <input {...register('telephone')} className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                <input type="email" {...register('email')} className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Diocèse</label>
                <Select
                  options={dioceses.map(d => ({ value: d.id, label: d.diocese }))}
                  value={selectedDiocese}
                  onChange={async (opt) => {
                    setSelectedDiocese(opt);
                    setValue('diocese_id', opt ? opt.value : null);
                    // Réinitialiser la paroisse si le diocèse change
                    if (!opt) {
                      setValue('paroisse_id', null);
                      setSelectedParoisse(null);
                      setParoissesCache([]);
                    } else {
                      // Charger les paroisses du diocèse sélectionné
                      const res = await paroissesService.getAll({ diocese_id: opt.value });
                      setParoissesCache(res.data.data || []);
                    }
                  }}
                  isClearable
                  placeholder="Sélectionner un diocèse"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Paroisse</label>
                <Select
                  isDisabled={!selectedDiocese}
                  options={paroissesCache.map(p => ({ value: p.id, label: p.paroisse }))}
                  value={selectedParoisse}
                  onChange={(opt) => {
                    setSelectedParoisse(opt);
                    setValue('paroisse_id', opt ? opt.value : null);
                  }}
                  onMenuOpen={async () => {
                    if (selectedDiocese && paroissesCache.length === 0) {
                      const res = await paroissesService.getAll({ diocese_id: selectedDiocese.value });
                      setParoissesCache(res.data.data || []);
                    }
                  }}
                  isClearable
                  isSearchable
                  placeholder={selectedDiocese ? "Sélectionner une paroisse" : "Sélectionnez d'abord un diocèse"}
                  classNamePrefix="react-select"
                  styles={selectStyles}
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="mb-6">
            <legend className="text-lg font-semibold mb-2 dark:text-gray-200">Parents</legend>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lier des parents</label>
              <Select
                isMulti
                isSearchable
                options={parents.map(parent => ({
                  value: String(parent.id),
                  label: `${parent.nom} ${parent.prenom} (${parent.type_parente})`
                }))}
                value={parents
                  .filter(parent => selectedParents.includes(String(parent.id)))
                  .map(parent => ({
                    value: String(parent.id),
                    label: `${parent.nom} ${parent.prenom} (${parent.type_parente})`
                  }))}
                onChange={opts => setSelectedParents(opts.map(opt => opt.value))}
                placeholder="Rechercher ou sélectionner un parent"
                classNamePrefix="react-select"
                styles={selectStyles}
              />
              <button type="button" className="mt-2 text-primary-600 dark:text-primary-400 hover:underline" onClick={() => setShowParentModal(true)}>
                + Ajouter un parent
              </button>
              <div className="mt-2 flex flex-wrap gap-2">
                  {selectedParents.map(pid => {
                    const p = parents.find(par => String(par.id) === pid);
                    if (!p) return null;
                    let badgeClass = "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200";
                    if (p.type_parente && p.type_parente.toLowerCase().includes("père")) {
                      badgeClass = "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border border-blue-300 dark:border-blue-700";
                    } else if (p.type_parente && p.type_parente.toLowerCase().includes("mère")) {
                      badgeClass = "bg-pink-100 dark:bg-pink-900 text-pink-800 dark:text-pink-200 border border-pink-300 dark:border-pink-700";
                    }
                    return (
                      <span key={pid} className={`px-2 py-1 rounded text-sm font-medium ${badgeClass}`}>
                        {p.nom} {p.prenom} ({p.type_parente})
                      </span>
                    );
                  })}
              </div>
            </div>
          </fieldset>

          <div className="flex justify-end space-x-2">
            <button type="button" className="px-4 py-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600" onClick={onClose}>Annuler</button>
            <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700" disabled={loading}>
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
        {showPaysModal && (
          <PaysModal
            onClose={() => setShowPaysModal(false)}
            onSuccess={async (newPays) => {
              setShowPaysModal(false);
              const res = await api.get('/pays');
              setPaysList(res.data.data || []);
              if (newPays && newPays.nom_pays) {
                setValue('nationalite', newPays.nom_pays);
              }
            }}
          />
        )}
        {showParentModal && (
          <ParentModal
            onClose={() => setShowParentModal(false)}
            onSuccess={async (newParent) => {
              setShowParentModal(false);
              const res = await api.get('/parents');
              setParents(res.data.data || []);
              if (newParent && newParent.id) {
                setSelectedParents(prev => [...prev, String(newParent.id)]);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default EleveModal;
